import { logError } from "@/shared/utils/datadog-logger";
import { useEffect } from "react";

/**
 * Catch resource loading errors (images, scripts, stylesheets).
 *
 * These errors do NOT bubble, so we must listen in the capture phase.
 * They are plain Events (not ErrorEvents) where event.target is the
 * element that failed to load.
 *
 * Runtime JS errors and unhandled promise rejections are already
 * captured by the Datadog SDK via `forwardErrorsToLogs: true`.
 */
const handleResourceError = (event: Event) => {
    if (event instanceof ErrorEvent) return;

    const target = event.target;
    if (!target || target === window || !(target instanceof HTMLElement)) return;

    const src = (target as HTMLImageElement | HTMLScriptElement).src || (target as HTMLLinkElement).href;

    logError("Resource load error", new Error(`Failed to load ${target.tagName}: ${src}`), {
        tagName: target.tagName,
        src,
    });
};

/**
 * Global error handler hook that supplements Datadog and ErrorBoundary:
 *
 * Datadog SDK catches:
 * - Runtime JS errors (via window.onerror)
 * - Unhandled promise rejections (via window.onunhandledrejection)
 * - Network errors (XHR/fetch)
 * - console.* calls (configured with `forwardConsoleLogs`)
 *
 * ErrorBoundary catches:
 * - Rendering errors
 * - Lifecycle method errors
 * - Constructor errors
 *
 * This hook catches what neither covers:
 * - Resource loading failures (images, scripts, stylesheets)
 */
export function useGlobalErrorHandler() {
    useEffect(() => {
        window.addEventListener("error", handleResourceError, true);

        return () => {
            window.removeEventListener("error", handleResourceError, true);
        };
    }, []);
}
